import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMobileAuth } from '../MobileAuthProvider';
import { apiBase, authHeaders } from '../../utils/authHeaders';
import { Bell, Lock, Wifi, WifiOff } from 'lucide-react';

/** Connection strip above the role tab bar (MobileTabBar owns navigation). Alerts and Security stay one tap away. */
const MobileNavigation = () => {
  const navigate = useNavigate();
  const { role, isOnline, token } = useMobileAuth();
  const [unreadCount, setUnreadCount] = useState(0);

  const loadUnreadCount = useCallback(async () => {
    const locationId = localStorage.getItem('current_location_id') || '';
    if (!token || !locationId) { setUnreadCount(0); return; }
    try {
      const response = await fetch(`${apiBase()}/api/mobile/notifications/unread-count`, {
        headers: authHeaders({ token, locationId }),
      });
      if (!response.ok) return;
      const data = await response.json();
      if ((localStorage.getItem('current_location_id') || '') === locationId) setUnreadCount(Number(data.unread_count) || 0);
    } catch { /* Inbox remains available even when its badge cannot refresh. */ }
  }, [token]);

  useEffect(() => {
    loadUnreadCount();
    window.addEventListener('locationChanged', loadUnreadCount);
    window.addEventListener('organizationChanged', loadUnreadCount);
    window.addEventListener('storage', loadUnreadCount);
    window.addEventListener('notificationsChanged', loadUnreadCount);
    window.addEventListener('focus', loadUnreadCount);
    return () => {
      window.removeEventListener('locationChanged', loadUnreadCount);
      window.removeEventListener('organizationChanged', loadUnreadCount);
      window.removeEventListener('storage', loadUnreadCount);
      window.removeEventListener('notificationsChanged', loadUnreadCount);
      window.removeEventListener('focus', loadUnreadCount);
    };
  }, [loadUnreadCount]);

  const link = 'min-h-[44px] inline-flex items-center gap-1 px-2 text-xs';
  return (
    <div
      className={`flex items-center justify-between px-4 border-t border-surface-border text-xs ${isOnline ? 'bg-primary-50 text-primary-700' : 'bg-warn/10 text-accent-800'}`}
      style={{ marginBottom: 'calc(3.75rem + env(safe-area-inset-bottom, 0px))' }}
      data-testid="mobile-connection-strip"
    >
      <span className="flex items-center gap-1">
        {isOnline ? <Wifi className="w-3 h-3" /> : <WifiOff className="w-3 h-3" />}
        {isOnline ? 'Connected' : 'Offline'} · {String(role).replace('_', ' ')}
      </span>
      <span className="flex items-center">
        <button type="button" className={`${link} relative`} onClick={() => { navigate('/mobile/notifications'); loadUnreadCount(); }} data-testid="mobile-nav-alerts">
          <Bell className="w-4 h-4" /> Alerts
          {unreadCount > 0 && <span aria-label={`${unreadCount} unread notifications`} className="ml-1 min-w-4 rounded-full bg-danger px-1 text-center text-[10px] leading-4 text-white">{unreadCount > 99 ? '99+' : unreadCount}</span>}
        </button>
        <button type="button" className={link} onClick={() => navigate('/mobile/security')} data-testid="mobile-nav-security">
          <Lock className="w-4 h-4" /> Security
        </button>
      </span>
    </div>
  );
};

export default MobileNavigation;
